import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataEditor, GridCellKind } from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import { folioService } from '../../../services/folioService';
import { useTheme } from '../../../context/ThemeContext';

// Folio · Model — the company's statements as the CURRENT MODEL serves them (GET folio/model/), in a Glide Data Grid:
// label column frozen, newest period on the right (scrolled into view on load), Excel-style indent from the company's
// latest calculation tree, totals bold under an accountant's rule (a double rule above a grand total) and collapsible
// (▾ / ▸), negatives in parentheses, three independent markers (changed since first reported = gold corner — worded by
// CAUSE when the block carries `cause_labels` (FOLIO_CAUSE_LABELS, #264): Recast / Corrected / Changed since first reported;
// "Restated" otherwise, pending review = amber left
// bar whose panel links to its question, derived = dot) and every blank drawn "—" with its reason in the side panel.
// The unit ($M / $BN) sits in the frozen header beside the figures; the grid takes the height left on screen (one
// scroll owner). Range-select + Cmd/Ctrl+C copies plain numbers straight into Excel.
//
// PHONES (< 768 px, styling pass 2026-10-25, owner: "loads in billions … the first column a bit shorter so you can see
// more periods"): $bn by default (the reader's own choice is remembered per device), a label column of ~40 % of the grid
// whose labels WRAP onto two lines (a row is taller only where its label needs it), 8 px indent steps, and figure
// columns sized to their content in the active unit — at 375 px at least three figure columns stay fully visible.
// Desktop keeps its 300 px label column and $m.
//
// NOT SORTABLE, deliberately — the one exception to the "every table is sortable" house rule: a financial statement
// is read in the company's own row order (a total sits under the rows it adds up); sorting it would destroy what it
// says. Added rows come from a FIXED menu (right-click a row, or its ⋯) and are computed on the server
// (services/folio_rows.py) — never a formula typed here.
//
// EXPORT (beside the controls; an icon on a phone): GET folio/export.xlsx — the three statements of the periods on
// screen, in the units on screen, this user's added rows as live Excel formulas (services/folio_export.py).

const STATEMENTS = [{ key: 'IS_CI', label: 'Income' }, { key: 'BS', label: 'Balance sheet' }, { key: 'CF', label: 'Cash flow' }];
const GRANS = [{ key: 'annual', label: 'Annual' }, { key: 'interim', label: 'Quarterly' }];
const UNITS = [{ key: 'm', label: '$m' }, { key: 'b', label: '$bn' }];
// the two presentations (FOLIO_TWO_BASIS_VIEW, #271): shown only when the company has a recast / corrected event. `auto` (the
// default) picks, per recast column, the basis with fewer blank cells; a column stays on ONE basis and says which.
const BASES = [{ key: 'auto', label: 'Auto', full: 'Auto — per recast column, the presentation with fewer blanks' },
  { key: 'presented', label: 'Now', desk: 'Presented', full: 'As presented now (the latest presentation)' },
  { key: 'first_reported', label: 'First', desk: 'First reported', full: 'As first reported (the actuals)' }];
const TITLE = { IS_CI: 'Income statement', BS: 'Balance sheet', CF: 'Cash flow' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const KIND = {
  derived_Q2: 'Derived: six months − first quarter', derived_Q3: 'Derived: nine months − six months',
  derived_Q4: 'Derived: year − nine months', release_Q4: "From the company's own earnings release",
  release_Q: "From the company's own earnings release", implied: "Solved from the company's own equation, confirmed by a second witness",
  as_reported_Q: 'As originally reported — the year was later redefined', residual_Q4: 'Year − three quarters (holds the definition change)',
  derived_Q4_presented: 'Year − nine months, both re-presented on the same recast basis',
  fold: 'Folded into another row by a later definition', comparable: 'Comparable series across a definition change',
  instant_Q2: 'Balance at quarter end', instant_Q3: 'Balance at quarter end', instant_Q4: 'Balance at year end',
  printed: "As printed in the filing for this date — that filing's own row, label and order",
  tagged_Q4: "The 10-K's own tagged Q4", restated_Q: 'Restated quarter', bridge: 'Definition change: restated year − these quarters',
  recast_printed: "Printed by the company on the recast basis (the recast filing's own quarter); its four quarters add to its printed year",
  first_reported_Q4_derived: 'Original year − original nine months, both first printed on one basis; the quarters as first reported add to that year',
};
// the gold corner's wording by cause (blocks with `cause_labels`; #264) — "Restated" is an error correction only
const CAUSE = { recast: 'Recast (discontinued operations)', corrected: 'Corrected', '': 'Changed since first reported' };
const causeLabel = (c) => CAUSE[c] || CAUSE[''];
// with cause labels on, the kinds that said "restated" stop saying it (the cause of these two is not established)
const KIND_NEUTRAL = { restated_Q: 'Quarter on the later definition', bridge: 'Definition change: year − these quarters' };
const WHY = {
  basis_break: 'Two definitions meet — the quarter is not derivable on one basis',
  recast_quarters_not_reprinted: 'The year was re-presented on a recast basis, its nine months never were — no Q4 on one basis',
  recast_basis_not_proven: 'The year and nine months are not proven to be on the same recast basis',
  recast_column_unproven: "The recast-basis Q4 does not satisfy the statement's own equations",
  quarters_not_nine_months: 'The quarters we serve do not add to the nine months the company printed',
  missing_input: 'An input of the derivation is missing', input_null: 'An input of the derivation is missing',
  folded_into: 'Folded into another row in this period', superseded: "Superseded: the latest filing's layout drops this row here",
  convention_mixed: 'The two inputs were filed in opposite sign conventions', sign_unresolved: 'An input’s sign is an open question',
  non_additive: 'Per-share figures and share counts do not add up across quarters',
  ambiguous_chain: 'Two lines of this row disagree from the same moment — not picked',
  first_reported_already_recast: 'The company first reported this year already recast, after its quarters were reported on the original basis — no figure as first reported that adds up',
  original_filing_not_loaded: "The company's original filing for this period is not in the loaded window",
  not_in_original_report: "The company's original report did not present this row for this period",
  no_original_basis_figure: 'No figure on the original basis exists for this period',
  recast_quarter_not_printed: 'The company did not print this quarter on the recast basis — only the recast year is known; the original quarter is shown as first reported',
  disposal_not_tagged: "The company does not tag the disposal group's revenue for this period",
  disposal_year_not_tied: 'The tagged group figure for the year does not tie to first reported − presented now, so no quarter is derived from it',
  disposal_year_not_checkable: 'The year cannot be checked against the tagged group figure (first reported or recast year missing), so no quarter is derived',
  recast_quarters_basis_unproven: "The printed recast quarters are not proven to be on one basis (they do not add to the printed year or nine months), so none is used to derive the group's revenue",
  recast_year_not_printed: 'The company did not print this year on the recast basis',
  period_not_re_presented: 'This period was not re-presented: first reported equals presented now, nothing to deduct',
  not_in_printed_column: "The filing this column is read from does not print this row",
  printed_in_another_row: "The filing prints this concept in another row of this grid — see that row",
  row_not_in_this_presentation: "This row is printed in the other presentation's filing only",
  no_comparable_period: 'No column for the comparable period', zero_base: 'The base is zero',
  non_positive_base: 'Growth needs both ends positive', row_gone: 'The row this was added under is no longer in the model',
  // statement polish (#281): every blank reason has words here AND in the export (services/blank_reasons.py)
  absent_row_pending: 'The 10-Qs print rows the 10-K lumps into another row, so this fourth quarter would be on two definitions — blank until the question of which row holds them is decided',
  absent_row_unknown: 'The 10-Qs print rows the 10-K lumps into another row, and the filings do not say which — the fourth quarter is not shown on two definitions',
  recast_split_not_printed: 'The company did not split this recast quarter (Products / Services …) — the first-reported rows carry the split',
  // an original-basis Q4 never removed (#284, services/original_q4.py)
  first_reported_q4_column_fails: "The original-basis Q4 (year − nine months as first printed) does not satisfy the statement's own equations, so it is not shown",
  first_reported_quarters_not_nine_months: 'The quarters as first reported do not add to the nine months the company first printed, so no original Q4 is derived',
  first_reported_inputs_amended: 'A later original filing changed the first-printed year or nine months, so no original Q4 is derived',
  first_reported_row_year_differs: "The row's own first-reported year is not the year of the one tag the filings cite for its fourth quarter (a row that joins several tags), so no original Q4 is derived",
  first_reported_inputs_not_cited: 'The filings do not cite one year figure and one nine-month figure of one tag for this row, so no original Q4 is derived',
};

// the platform's own tokens (tailwind.config.js / index.css) with the house GOLD in both themes (Folio overrides the
// platform's light cobalt, `.folio-accent` in index.css — a darker gold #8a6d2f, 5.0:1 on white); every text colour
// reads ≥ 4.5:1 on its surface (WCAG AA, UI fixes 2026-10-27: light faint #9ca3af was 2.5:1, dark faint 3.6:1). Hover /
// selected tints are solid blends (a canvas cell needs an opaque fill)
const PALETTE = {
  dark: { bg: '#0d1117', surface: '#161b22', surface2: '#1a2029', line: 'rgba(148,163,184,0.10)',
    rule: 'rgba(148,163,184,0.38)', text: '#e5e7eb', strong: '#f9fafb', muted: '#9ca3af', faint: '#8b949e',
    accent: '#c9a96e', accentLight: 'rgba(201,169,110,0.18)', pos: '#4ade80', neg: '#f87171', warn: '#fcd34d',
    hover: '#121820', hoverLabel: '#1b212a', sel: '#1d1c19', selLabel: '#24221d' },
  light: { bg: '#ffffff', surface: '#f9fafb', surface2: '#f3f6fa', line: 'rgba(15,23,42,0.07)',
    rule: 'rgba(15,23,42,0.35)', text: '#1f2937', strong: '#0b1220', muted: '#4b5563', faint: '#6b7280',
    accent: '#8a6d2f', accentLight: 'rgba(138,109,47,0.14)', pos: '#15803d', neg: '#b91c1c', warn: '#b45309',
    hover: '#f8fafc', hoverLabel: '#f1f5f9', sel: '#faf6ee', selLabel: '#f3ecdd' },
};
const MONO = "'SF Mono','Fira Code',ui-monospace,Menlo,monospace";
const SANS = "Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";
const PAD = 10;              // a figure's right padding (Glide's cellHorizontalPadding); the marker dot sits inside it

const scaleOf = (unit, units) => (unit === 'currency' ? (units === 'b' ? 1e-9 : 1e-6) : unit === 'shares' ? 1e-6 : 1);
const decimalsOf = (unit, units) => (unit === 'per_share' ? 2 : unit === 'currency' && units === 'b' ? 1
  : unit === 'currency' || unit === 'shares' ? 0 : 2);
const unitName = (unit, units) => (unit === 'currency' ? (units === 'b' ? 'USD billions' : 'USD millions')
  : unit === 'shares' ? 'millions of shares' : unit === 'per_share' ? 'USD per share' : unit);

function scaled(raw, unit, units) {
  if (raw == null) return null;
  const x = Number(raw) * scaleOf(unit, units);
  return Number.isFinite(x) ? x : null;
}

const isZeroText = (text) => Number(text.replace(/[^0-9.]/g, '')) === 0;
function paren(x, text) { return x < 0 && !isZeroText(text) ? `(${text})` : text; }

function fmtNum(raw, unit, units) {
  const x = scaled(raw, unit, units);
  if (x == null) return '—';
  const d = decimalsOf(unit, units);
  return paren(x, Math.abs(x).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }));
}

// the exact figure, for the side panel (a $bn cell may round a small figure to 0.0)
function fmtExact(raw, unit) {
  const x = scaled(raw, unit, 'm');
  if (x == null) return '—';
  const d = unit === 'per_share' ? 2 : 3;
  return paren(x, Math.abs(x).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d }));
}

function fmtAdded(raw, a, units) {
  if (raw == null) return '—';
  const x = Number(raw);
  if (a.unit === 'percent') return paren(x, `${Math.abs(x * 100).toFixed(1)}%`);
  if (a.unit === 'ratio') return paren(x, `${Math.abs(x).toFixed(2)}×`);
  return fmtNum(raw, a.unit, units);
}

// a figure column's text: a positive number carries a trailing space as wide as ")" so its digits line up with the
// parenthesised negatives above and below it (the font is monospaced)
const aligned = (text) => (text.endsWith(')') || text === '—' || text === '' ? text : `${text} `);

// the plain number Excel receives on copy (display scale, no parentheses or separators)
function copyOf(raw, unit, units) {
  if (raw == null) return '';
  if (unit === 'percent' || unit === 'ratio') return String(Number(raw));
  const x = scaled(raw, unit, units);
  return x == null ? '' : String(Number(x.toFixed(decimalsOf(unit, units) + 2)));
}

function colHead(p, gran) {
  // fy / fp when the filing tags the column, else the label the server inferred from the contiguous quarters (lfy / lfp), else the end year
  const yy = String(p.fy || p.lfy || p.end.slice(0, 4)).slice(2);
  if (gran === 'annual') return `FY${yy}`;
  const fp = p.fp || p.lfp || '';
  const q = /^Q[1-4]$/.test(fp) ? fp : (p.instant ? 'Q4' : 'Q');
  return `${q} FY${yy}`;
}
const endLabel = (p) => `${MONTHS[Number(p.end.slice(5, 7)) - 1]} ${p.end.slice(0, 4)}`;
const periodText = (p, gran) => `${colHead(p, gran)} · ${p.instant ? 'balance at' : 'period ending'} ${p.end}`;

// the header badge of a column an event covers (#271): the basis the column is on; a phone gets the short wording
function badgeText(n, phone) {
  const warn = n.printed && n.printed.foots === false ? ' ⚠' : '';          // a balance-sheet column read from its filing that does not foot
  if (n.used === 'first_reported') return (phone ? 'As reported' : 'As first reported') + warn;
  if (n.label === 'Changed since first reported') return (phone ? 'Changed' : n.label) + warn;
  return (n.needs_review ? `${n.label} · review` : n.label) + warn;
}

const secUrl = (cik, acc) => `https://www.sec.gov/Archives/edgar/data/${cik}/${acc.replace(/-/g, '')}/${acc}-index.htm`;

// an export's failure in plain words: the server's own detail (read out of the blob the request asked for), else by status
async function exportError(e) {
  const st = e?.response?.status;
  try {
    const d = e?.response?.data;
    const t = d && typeof d.text === 'function' ? await d.text() : '';
    const detail = t ? JSON.parse(t)?.detail : '';
    if (detail && st !== 500) return detail;
  } catch { /* not JSON: fall through */ }
  if (st === 429) return 'Too many exports in the last hour — try again later.';
  if (st === 409) return 'The statements are being refreshed — try the export again shortly.';
  return 'The export could not be made. Try again.';
}

// the server's file name (Content-Disposition), else one of the same shape
function downloadName(headers, ticker, gran, units) {
  const m = /filename="([^"]+)"/.exec(headers?.['content-disposition'] || '');
  return m ? m[1] : `${ticker}-folio-${gran === 'annual' ? 'annual' : 'quarterly'}-${units === 'b' ? 'usd-bn' : 'usd-m'}.xlsx`;
}

function DownloadIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2.5v7.5M4.75 7 8 10.25 11.25 7M3 13.5h10" />
    </svg>
  );
}

const PHONE_QUERY = '(max-width: 767px)';
const isPhoneNow = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_QUERY).matches;
function usePhone() {
  const [phone, setPhone] = useState(isPhoneNow);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(PHONE_QUERY);
    const on = () => setPhone(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return phone;
}

// the width of an element, live (ResizeObserver) — the grid's own container, so the columns follow the space it really
// has, not the window
function useWidth(ref) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

// THE GRID'S HEIGHT = the space left on screen (UI fixes 2026-10-27): window height − the grid's top in the page −
// what sits under it − the layout's bottom padding, so the page itself does not scroll and the grid is the ONE scroll
// owner (a fixed 64vh grid ended 58 px below a 375×812 phone's edge and 24 px below a 768×1024 tablet's: page and grid
// scrolled inside each other and the last rows were out of reach). Never under 320 px (a short landscape phone then
// scrolls the page once, the grid keeps its rows; 260 px on a phone). Re-measured on resize and whenever the content above / below moves.
const GRID_MIN_H = 320;       // a phone: 260 (its chrome is taller relative to the screen)
function useFitHeight(ref, belowRef) {
  const [h, setH] = useState(480);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let id = 0;
    const fit = () => {
      cancelAnimationFrame(id);
      id = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const top = r.top + window.scrollY;
        const page = document.documentElement;
        // everything under the grid: its own notes / legend and the page's bottom padding (page height − its bottom)
        const under = Math.max(0, page.scrollHeight - (r.bottom + window.scrollY));
        const vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
        const next = Math.max(isPhoneNow() ? 260 : GRID_MIN_H, Math.floor(vh - top - under - 8));
        setH((cur) => (Math.abs(cur - next) > 1 ? next : cur));
      });
    };
    fit();
    window.addEventListener('resize', fit);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (ro) { ro.observe(document.body); if (belowRef.current) ro.observe(belowRef.current); }
    return () => { cancelAnimationFrame(id); window.removeEventListener('resize', fit); if (ro) ro.disconnect(); };
  }, [ref, belowRef]);
  return h;
}

// the per-device units choice: the reader's own pick wins; otherwise $bn on a phone, $m on a desktop
const UNITS_KEY = 'folio:units';
function readUnits() {
  try { const u = localStorage.getItem(UNITS_KEY); return u === 'm' || u === 'b' ? u : null; } catch { return null; }
}
function writeUnits(u) {
  try { localStorage.setItem(UNITS_KEY, u); } catch { /* a private window: this session only */ }
}

// one offscreen canvas for measuring text exactly as the grid will draw it
let measureCtx = null;
function measure(font, text) {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = font;
  return measureCtx.measureText(text).width;
}

// `text` cut to fit ``avail`` px with an ellipsis (binary search on length; a canvas has no text-overflow)
function fitText(font, text, avail) {
  if (avail <= 0) return '';
  if (measure(font, text) <= avail) return text;
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (measure(font, `${text.slice(0, mid).trimEnd()}…`) <= avail) lo = mid; else hi = mid - 1;
  }
  return lo ? `${text.slice(0, lo).trimEnd()}…` : '…';
}

// a label on at most two lines: words fill the first line, the rest goes to the second, cut with an ellipsis there
function wrapTwo(font, text, avail) {
  if (measure(font, text) <= avail) return [text];
  const words = text.split(/\s+/);
  let first = '';
  let i = 0;
  for (; i < words.length; i += 1) {
    const next = first ? `${first} ${words[i]}` : words[i];
    if (measure(font, next) > avail) break;
    first = next;
  }
  if (!first) return [fitText(font, text, avail)];               // one word longer than the line: cut it
  return [first, fitText(font, words.slice(i).join(' '), avail)];
}

const PHONE_LABEL_MIN = 128;   // a phone's label column never narrower: two lines of ~15 characters each

// layout constants per form factor
const LAYOUT = {
  // phone: ``dots`` = the text room kept for ⋯; ``dotsHit`` / ``toggleHit`` = the TAP zones (44 px, the touch-target
  // minimum) — wider than what is drawn, so a label keeps its room
  phone: { labelFont: 12, tagFont: 9, lineH: 14, rowH: 30, rowH2: 42, indent: 8, toggle: 14, dots: 26, dotsHit: 44,
    toggleHit: 44, figFont: 12, headFont: 11, minFig: 50 },
  // a desktop keeps today's 92 px figure columns (wider only if a figure needs it) and its 300 px label column
  desk: { labelFont: 13, tagFont: 10, lineH: 16, rowH: 32, rowH2: 32, indent: 14, toggle: 18, dots: 30, dotsHit: 34,
    toggleHit: 0, figFont: 13, headFont: 12, minFig: 92 },
};

function Seg({ label, items, value, onChange, phone, desk }) {
  return (
    <div className="flex flex-none items-center gap-2">
      {!phone && <span className="text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">{label}</span>}
      <div className="inline-flex overflow-hidden rounded-md border border-gray-200 dark:border-gray-700/80" role="group" aria-label={label}>
        {items.map((it, i) => (
          <button key={it.key} type="button" aria-pressed={value === it.key} onClick={() => onChange(it.key)}
            aria-label={it.full || undefined} title={it.full || undefined}
            className={`whitespace-nowrap px-2.5 py-1 text-[11px] tracking-wide transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold/70 ${phone ? 'min-h-[44px] min-w-[44px] px-3' : ''} ${i ? 'border-l border-gray-200 dark:border-gray-700/80' : ''} ${value === it.key
              ? 'bg-brand-gold/10 font-medium text-brand-gold' : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800/60'}`}>{desk && !phone && it.desk ? it.desk : it.label}</button>
        ))}
      </div>
    </div>
  );
}

const storeKey = 'folio:collapsed';
function readCollapsed() {
  try { return JSON.parse(localStorage.getItem(storeKey) || '{}') || {}; } catch { return {}; }
}
function writeCollapsed(v) {
  try { localStorage.setItem(storeKey, JSON.stringify(v)); } catch { /* a private window: per-session only */ }
}

// the grid while it loads (also through the server's 202 "building" retries): a header and rows that shimmer
function GridSkeleton({ phone, building }) {
  const rows = [62, 48, 70, 40, 56, 66, 44, 58, 52, 68, 46, 60];
  const figs = phone ? 3 : 7;
  return (
    <div className="flex h-full flex-col" aria-busy="true" aria-label="Loading the statement">
      <div className="flex h-11 items-center gap-3 border-b border-gray-200 px-3 dark:border-gray-800">
        <div className="skeleton h-2.5 w-28" />
        <div className="ml-auto flex gap-3">{Array.from({ length: figs }, (_, i) => <div key={i} className="skeleton h-2.5 w-10" />)}</div>
      </div>
      {rows.map((w, r) => (
        <div key={r} className="flex h-8 items-center gap-3 border-b border-gray-100 px-3 dark:border-gray-800/50">
          <div className="skeleton h-2" style={{ width: `${phone ? w * 0.55 : w * 1.6}px` }} />
          <div className="ml-auto flex gap-3">{Array.from({ length: figs }, (_, i) => <div key={i} className="skeleton h-2 w-10 opacity-70" />)}</div>
        </div>
      ))}
      {building && <div className="px-3 py-2 text-xs text-gray-500">Building the statement — a few seconds the first time.</div>}
    </div>
  );
}

const asideCls = 'fixed inset-x-0 bottom-0 z-40 flex flex-col rounded-t-2xl border-t border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-[#161b22] md:inset-x-auto md:bottom-0 md:right-0 md:top-0 md:w-[360px] md:rounded-none md:border-l md:border-t-0';
const capCls = 'text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400';

function PanelHead({ title, sub, onClose }) {
  return (
    <div className="flex items-start gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-800">
      <div className="min-w-0">
        <h2 className="font-serif text-[17px] leading-snug text-gray-900 dark:text-gray-100">{title}</h2>
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{sub}</div>
      </div>
      <button type="button" onClick={onClose} aria-label="Close"
        className="-my-2 ml-auto min-h-[44px] rounded-md px-3 py-1 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 md:my-0 md:min-h-0 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800">Close</button>
    </div>
  );
}

// a tapped LABEL: the row's full name (a phone may cut it), its standard model line and the total it sits in
function LabelPanel({ row, data, units, onClose }) {
  const parent = row.parent ? data.rows.find((r) => r.id === row.parent) : null;
  return (
    <aside aria-live="polite" className={`${asideCls} max-h-[70%] md:max-h-none`}>
      <PanelHead title={row.label} sub={`${row.total ? 'Total' : 'Row'} · ${unitName(row.unit, units)}`} onClose={onClose} />
      <dl className="grid grid-cols-[auto,1fr] gap-x-5 gap-y-2.5 overflow-y-auto px-5 py-4 text-sm">
        {row.filed_label && (<><dt className={capCls}>Filed as</dt>
          <dd className="text-gray-800 dark:text-gray-200">{row.filed_label}</dd></>)}
        <dt className={capCls}>Standard line</dt>
        <dd className="text-gray-800 dark:text-gray-200">{row.standard || <span className="text-gray-500">not mapped</span>}</dd>
        <dt className={capCls}>Within</dt>
        <dd className="text-gray-800 dark:text-gray-200">{parent ? parent.label : <span className="text-gray-500">— (no total in the latest filing)</span>}</dd>
        {row.note && (<><dt className={capCls}>Note</dt><dd className="text-gray-600 dark:text-gray-400">{row.note}</dd></>)}
        {row.sdb && (<><dt className={capCls}>Basis</dt>
          <dd className="text-amber-700 dark:text-amber-300">{row.sdb.cells} figure{row.sdb.cells === 1 ? '' : 's'} of this row {row.sdb.kind === 'already_recast' ? 'are first prints made already on the recast basis' : row.sdb.kind === 'pre_disposal' ? 'are before the disposal group (as first reported); the total row is on the recast basis' : 'are shown as first reported'} — tap them for why.</dd></>)}
        <dt className={capCls}>Figures</dt>
        <dd className="text-gray-600 dark:text-gray-400">Tap a figure for how it was made and its filing.</dd>
      </dl>
    </aside>
  );
}

const CHECK_NAME = { REV: 'Revenue', NI: 'Net income', ASSETS: 'Assets', LIAB: 'Liabilities', LE: 'Liabilities + equity', EQ: 'Equity',
  CFO: 'Operating cash', CFI: 'Investing cash', CFF: 'Financing cash', CASHCHG: 'Net change in cash' };
const INV = [['assets', 'Total assets'], ['liabilities', 'Total liabilities'], ['liabilities_and_equity', 'Liabilities and equity'], ['equity', 'Total equity']];
const SIDE = { assets: 'Assets', liabilities: 'Liabilities', equity: 'Equity' };
const BASIS_NAME = { first_reported: 'As first reported', presented: 'As presented now' };

// a balance-sheet column read from the filings that print it (FOLIO_BS_PRINTED, #273): which filing each presentation is,
// whether it foots, this row in each, the bridge's invariants and the disposal group as the filings tag it
function PrintedBalance({ data, p, row, units }) {
  const m = data.bs_printed && data.bs_printed[p.key];
  if (!m) return null;
  const pair = (m.pairs || {})[row.id] || {};
  const b = m.bridge;
  const money = (v) => (v == null ? '—' : fmtNum(v, 'currency', units));
  const sideLine = (k) => {
    const s = b.sides[k];
    return `${SIDE[k]}: ${money(s.matched)} on rows in both, ${money(s.only_in_p)} only in the latest${Number(s.only_in_p_disposal_group) ? ` (of it ${money(s.only_in_p_disposal_group)} disposal group)` : ''}, ${money(s.only_in_f)} only in the first${s.ties === false ? ' — does not tie' : s.ties ? ' — ties' : ''}`;
  };
  const dgP = (b.disposal_group && b.disposal_group.p) || [];
  return (
    <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-800">
      <div className={capCls}>Balance sheet as printed</div>
      {['first_reported', 'presented'].map((k) => {
        const c = m[k];
        const v = pair[k === 'presented' ? 'presented' : 'first_reported'];
        return (
          <div key={k} className="text-[13px] text-gray-800 dark:text-gray-200">
            <div><span className="font-medium">{BASIS_NAME[k]}</span> — {c.form} filed {c.filed}
              {c.foots === false ? <span className="text-amber-700 dark:text-amber-300"> · does not foot ⚠</span> : c.foots ? <span className="text-gray-500"> · foots ✓</span> : null}
              {!c.own && k === 'first_reported' && <span className="text-gray-500"> · not the date's own report</span>}</div>
            <div className="font-mono tabular-nums text-gray-600 dark:text-gray-400">{v ? `${money(v.value)}  “${v.label}”` : 'this row is not printed there'}</div>
            {(c.failures || []).map((f) => (
              <div key={f.total} className="text-xs text-amber-700 dark:text-amber-300">{f.label}: printed {money(f.printed)}, its parts add to {money(f.sum)}</div>))}
          </div>);
      })}
      <table className="w-full text-xs">
        <thead><tr className="text-left text-gray-500"><th className="py-0.5 pr-2 font-normal">Invariant</th><th className="py-0.5 pr-2 text-right font-normal">First</th><th className="py-0.5 pr-2 text-right font-normal">Now</th><th className="py-0.5 text-right font-normal"> </th></tr></thead>
        <tbody>
          {INV.map(([k, name]) => { const v = b.invariants[k]; return (
            <tr key={k} className="text-gray-800 dark:text-gray-200">
              <td className="py-0.5 pr-2">{name}</td>
              <td className="py-0.5 pr-2 text-right font-mono tabular-nums">{money(v.f && v.f.value)}</td>
              <td className="py-0.5 pr-2 text-right font-mono tabular-nums">{money(v.p && v.p.value)}</td>
              <td className="py-0.5 text-right">{v.equal === false ? <span className="text-amber-700 dark:text-amber-300">differs ⚠</span> : v.equal ? '=' : <span className="text-gray-500">n/a</span>}</td>
            </tr>); })}
        </tbody>
      </table>
      {b.conflict && <div className="text-xs text-amber-700 dark:text-amber-300">The totals differ between the two presentations — a conflict flag for review; the column's label is unchanged.</div>}
      <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-400">{['assets', 'liabilities', 'equity'].map((k) => <li key={k}>{sideLine(k)}</li>)}</ul>
      {dgP.length > 0 && (
        <div className="text-xs text-gray-600 dark:text-gray-400">
          <div className="text-gray-800 dark:text-gray-200">Disposal group, as the latest filing tags it — inside its totals</div>
          {dgP.map((d, i) => <div key={i} className="flex justify-between gap-3"><span>{d.label}</span><span className="font-mono tabular-nums">{money(d.value)}</span></div>)}
          <div className="mt-1">In the first filing the same business sits inside the ordinary rows; the bridge does not split them.</div>
        </div>)}
    </div>
  );
}

// the provenance of a reconciliation figure (#274): filed = a tagged XBRL fact of a filing; printed = a quarter the company
// printed in a release / 10-K note that the model reads; derived = computed from those (formula + sources in the panel)
const TAGWORD = { filed: 'Filed — a tagged fact of the filing', printed: "Printed — the company's own printed quarter", derived: 'Derived — computed from the figures above' };
const TAGLETTER = { filed: 'f', printed: 'p', derived: 'd' };
// a figure shown on another basis than the rows around it (FOLIO_SHOW_DONT_BLANK #287): PRE = before the disposal group (as first
// reported), 1ST = as first reported where the presented basis has none, MIX = first reported already on the recast basis
const SDB_TAG = { pre_disposal: 'PRE', first_reported: '1ST', already_recast: 'MIX' };
const RECON_STATUS = { correction: 'A correction of an error: the later presentation − the first, exactly. Always marked needs review.',
  ties: "First reported − disposal group = presented now, exactly (within the filings' own rounding).",
  no_tie: 'Does NOT tie: first reported − disposal group is not presented now. Shown as filed, not reconciled.',
  difference_only: 'No group figure is tagged for this period: the whole recast difference, not split by quarter.',
  unchanged: 'Not re-presented: first reported equals presented now.', partial: 'Some figures are missing, so this column is not reconciled.' };

// the DISPOSAL GROUP under the income statement (FOLIO_DISPOSAL_BLOCK, #274): per fiscal year a recast re-presented, the group's
// own lines exactly as the filings TAG them (their own sign), never summed into a subtotal — outside the statement's totals
function DisposalBlock({ block, cik, units }) {
  const [sel, setSel] = useState(null);
  const years = block.years || [];
  if (!years.length) return null;
  const detail = sel && (() => {
    const y = years.find((x) => x.fy === sel.fy); const col = y?.columns.find((c) => c.key === sel.col);
    const ln = y?.groups[sel.g]?.lines[sel.l]; const cell = ln?.cells[sel.col];
    return cell ? { y, col, ln, cell } : null;
  })();
  return (
    <section aria-label="Disposal group, as filed" className="space-y-2 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-[#0d1117]">
      <div className="text-[11px] text-gray-500 dark:text-gray-400">
        Each line exactly as the filing tags it (its own sign). The lines are never added into a subtotal, so they need not foot —
        this block sits outside the statement's totals. <span className="font-mono">f</span> filed · <span className="font-mono">p</span> printed · <span className="font-mono">d</span> derived.
      </div>
      {years.map((y, yi) => (
        <details key={y.fy || y.end} open={yi === 0} className="group rounded-lg border border-gray-100 dark:border-gray-800/80">
          <summary className="flex min-h-[44px] cursor-pointer items-center gap-2 px-3 py-2 text-[13px] font-medium text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 dark:text-gray-200 md:min-h-0">
            <span aria-hidden="true" className="text-gray-500 transition-transform group-open:rotate-90">▸</span>
            {y.label} <span className="font-normal text-gray-500">year ended {y.end}</span>
            <span className="ml-auto hidden text-[11px] font-normal text-gray-500 md:inline">{y.groups.map((g) => g.label).filter((x, i, a) => a.indexOf(x) === i).slice(0, 2).join(' · ')}</span>
          </summary>
          <div className="scrollbar-thin overflow-x-auto px-1 pb-2">
            <table className="min-w-full border-collapse text-[12px]">
              <thead>
                <tr className="text-gray-500 dark:text-gray-400">
                  <th scope="col" className="sticky left-0 z-10 min-w-[150px] bg-white px-2 py-1 text-left font-normal dark:bg-[#0d1117]">Line</th>
                  {y.columns.map((c) => <th key={c.key} scope="col" className="whitespace-nowrap px-2 py-1 text-right font-mono font-medium text-gray-700 dark:text-gray-300">{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {y.groups.map((g, gi) => (
                  <React.Fragment key={g.key + gi}>
                    <tr><th scope="rowgroup" colSpan={y.columns.length + 1} className="sticky left-0 bg-gray-50 px-2 py-1 text-left text-[11px] font-medium uppercase tracking-[0.1em] text-gray-500 dark:bg-gray-900/60 dark:text-gray-400">{g.label}</th></tr>
                    {g.lines.map((ln, li) => (
                      <tr key={ln.concept + li} className="border-t border-gray-100 dark:border-gray-800/70">
                        <th scope="row" className="sticky left-0 z-10 bg-white px-2 py-1 pl-4 text-left text-[12px] font-normal text-gray-800 dark:bg-[#0d1117] dark:text-gray-200">{ln.label}{ln.memo && <span className="ml-1.5 block text-[10px] font-normal text-gray-500 dark:text-gray-400">memo — {ln.memo.text}</span>}</th>
                        {y.columns.map((c) => {
                          const cell = ln.cells[c.key];
                          if (!cell) return <td key={c.key} className="px-2 py-1 text-right font-mono text-gray-400 dark:text-gray-600">—</td>;
                          const on = sel && sel.fy === y.fy && sel.g === gi && sel.l === li && sel.col === c.key;
                          return (
                            <td key={c.key} className={`px-1 py-0.5 text-right ${on ? 'bg-brand-gold/10' : ''}`}>
                              <button type="button" onClick={() => setSel({ fy: y.fy, g: gi, l: li, col: c.key })}
                                aria-label={`${ln.label}, ${c.label}: ${fmtNum(cell.value, 'currency', units)} (${cell.tag})`}
                                className={`min-h-[36px] whitespace-nowrap rounded px-1 font-mono tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 md:min-h-0 ${cell.tag === 'derived' ? 'italic text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                {fmtNum(cell.value, 'currency', units)}<sup className="ml-0.5 text-[8px] text-gray-500">{TAGLETTER[cell.tag] || ''}</sup>{cell.note ? <span className="text-amber-700 dark:text-amber-300">*</span> : null}
                              </button>
                            </td>);
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {y.events.length > 0 && (
            <div className="px-3 pb-2 text-[11px] text-gray-500 dark:text-gray-400">
              From {y.events.map((e, i) => (
                <span key={e.accession}>{i ? ' · ' : ''}{cik ? <a href={secUrl(cik, e.accession)} target="_blank" rel="noopener noreferrer" className="text-brand-gold hover:underline">{e.form} {e.date}</a> : `${e.form} ${e.date}`}</span>))}
            </div>)}
        </details>
      ))}
      {detail && (
        <div role="status" className="rounded-lg bg-gray-50 px-3 py-2 text-[12px] text-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
          <span className="font-medium">{detail.ln.label}</span> · {detail.col.label} · <span className="font-mono tabular-nums">{fmtExact(detail.cell.value, 'currency')}</span> million
          <div className="mt-0.5 text-gray-500">{TAGWORD[detail.cell.tag]}{detail.cell.how ? ` — ${detail.cell.how}` : ''}</div>
          {detail.cell.note && <div className="mt-0.5 text-amber-700 dark:text-amber-300">{detail.cell.note}</div>}
          {(detail.cell.srcs || (detail.cell.src ? [detail.cell.src] : [])).map((a) => (
            <div key={a.accession} className="mt-0.5 text-[11px] text-gray-500">
              {cik ? <a href={secUrl(cik, a.accession)} target="_blank" rel="noopener noreferrer" className="text-brand-gold hover:underline">{a.form} · {a.date}</a> : `${a.form} · ${a.date}`}
              <span className="ml-2 font-mono">{a.accession}</span>
            </div>))}
        </div>)}
    </section>
  );
}

function SidePanel({ panel, data, gran, units, onClose, onOpenQuestion }) {
  if (!panel) return null;
  if (panel.label) return <LabelPanel row={panel.row} data={data} units={units} onClose={onClose} />;
  const { row, ci } = panel;
  const p = data.periods[ci];
  const c = row.cells[ci];
  const cik = data.company?.cik;
  const shown = c ? fmtNum(c.value, row.unit, units) : '—';
  const exact = c && c.value != null && row.unit === 'currency' ? fmtExact(c.value, row.unit) : null;
  return (
    <aside aria-live="polite" className={`${asideCls} h-[70%] md:h-auto`}>
      <PanelHead title={row.label} sub={periodText(p, gran)} onClose={onClose} />
      <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4 text-sm">
        <div>
          <div className={capCls}>Figure</div>
          <div className="mt-1.5 font-mono text-[28px] leading-none tabular-nums text-gray-900 dark:text-gray-100">{shown}</div>
          <div className="mt-1.5 text-xs text-gray-500">{unitName(row.unit, units)}
            {exact && exact !== shown && <> · exactly {exact} million</>}</div>
        </div>
        {(!c || c.value == null) && (
          <div className="rounded-lg bg-gray-50 px-3 py-2.5 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300">
            <span className="font-medium">Blank.</span> {c ? (WHY[c.null_reason] || c.null_reason || 'No figure for this period')
              : 'This row is not presented in this period — no filing gives it a figure here'} — never a guessed figure.
          </div>
        )}
        {c && (
          <dl className="grid grid-cols-[auto,1fr] gap-x-5 gap-y-2.5">
            <dt className={capCls}>How</dt>
            <dd className="text-gray-800 dark:text-gray-200">{c.recon ? (c.recon.tag ? TAGWORD[c.recon.tag] : 'Not available') : c.kind && c.kind !== 'direct' ? ((data.cause_labels || data.polish) && KIND_NEUTRAL[c.kind]) || KIND[c.kind] || c.kind : 'As filed by the company'}</dd>
            {c.recon && (c.recon.how || c.recon.kind) && (<><dt className={capCls}>Formula</dt>
              <dd className="text-gray-800 dark:text-gray-200">{c.recon.how}{c.recon.kind && KIND[c.recon.kind] ? ` (${KIND[c.recon.kind]})` : ''}</dd></>)}
            {c.recon && c.recon.status && RECON_STATUS[c.recon.status] && (<><dt className={capCls}>Column</dt>
              <dd className={c.recon.status === 'no_tie' ? 'text-amber-700 dark:text-amber-300' : 'text-gray-800 dark:text-gray-200'}>{RECON_STATUS[c.recon.status]}</dd></>)}
            {c.polish && c.polish.was != null && (<><dt className={capCls}>First reported</dt>
              <dd className="text-gray-800 dark:text-gray-200"><span className="font-mono tabular-nums">{fmtNum(c.polish.was, row.unit, units)}</span> <span className="text-gray-500">— the quarter as originally reported, replaced here by the figure the company printed on the recast basis</span></dd></>)}
            {c.polish && c.polish.was == null && (<><dt className={capCls}>First reported</dt>
              <dd className="text-gray-500">{WHY[c.polish.was_reason] || 'No figure was shown here before'} — the company's recast filing prints this quarter.</dd></>)}
            {c.recon && c.recon.first != null && (<><dt className={capCls}>Presentations</dt>
              <dd className="text-gray-800 dark:text-gray-200"><span className="font-mono tabular-nums">{fmtNum(c.recon.first, row.unit, units)}</span> as first reported → <span className="font-mono tabular-nums">{fmtNum(c.recon.latest, row.unit, units)}</span> as corrected
                {c.recon.needs_review && <span className="text-amber-700 dark:text-amber-300"> — needs review</span>}</dd></>)}
            {c.first != null && (<>
              <dt className={capCls}>First reported</dt>
              <dd className="text-gray-800 dark:text-gray-200"><span className="font-mono tabular-nums">{fmtNum(c.first, row.unit, units)}</span> <span className="text-gray-500">— {data.cause_labels || data.polish ? causeLabel(c.cause).toLowerCase() : 'restated'} since</span></dd>
            </>)}
            {c.pending && (<><dt className={capCls}>Status</dt>
              <dd className="text-amber-700 dark:text-amber-300">
                <div>Pending review — held until a question is decided</div>
                {/* the question(s) holding this figure (the block carries their ids); none matched → the company's list */}
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {(c.questions || []).length ? c.questions.map((q) => (
                    <button key={q} type="button" onClick={() => onOpenQuestion(q)}
                      className="inline-flex min-h-[44px] items-center rounded-md border border-amber-600/40 px-3 text-xs font-medium text-amber-800 hover:bg-amber-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 dark:text-amber-200 md:min-h-0 md:py-1.5">
                      Open question {data.company?.ticker}-{q} →</button>
                  )) : (
                    <button type="button" onClick={() => onOpenQuestion(null)}
                      className="inline-flex min-h-[44px] items-center rounded-md border border-amber-600/40 px-3 text-xs font-medium text-amber-800 hover:bg-amber-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 dark:text-amber-200 md:min-h-0 md:py-1.5">
                      Open {data.company?.ticker}'s questions →</button>
                  )}
                </div>
              </dd></>)}
            {c.qsum_gap && (<><dt className={capCls}>Quarters</dt>
              <dd className="text-gray-800 dark:text-gray-200">
                <span className="text-amber-700 dark:text-amber-300">{c.qsum_gap.status === 'closed_by_reconciliation' ? 'Closed by the reconciliation rows' : 'Quarters on mixed bases'}</span>
                <span className="block text-xs text-gray-500">The four quarters add to <span className="font-mono tabular-nums">{fmtNum(c.qsum_gap.quarters, row.unit, units)}</span>; the year is{' '}
                  <span className="font-mono tabular-nums">{fmtNum(c.qsum_gap.year, row.unit, units)}</span> (gap <span className="font-mono tabular-nums">{fmtNum(c.qsum_gap.gap, row.unit, units)}</span>).{' '}
                  {c.qsum_gap.via && c.qsum_gap.via.case === 'quarters_are_first_reported' && 'The quarters add to the year as first reported; the recast year differs by the disposal group’s tagged revenue (reconciliation rows).'}
                  {c.qsum_gap.via && c.qsum_gap.via.case === 'one_quarter_not_recast' && `The gap is exactly Q${c.qsum_gap.via.quarter}’s disposal revenue (the year’s tagged figure less the other quarters’) — that quarter is shown as first reported (reconciliation row).`}
                  {!c.qsum_gap.via && 'The quarters and the year come from different presentations (or, as first reported, the company re-presented earlier quarters within the year). Nothing is allocated.'}</span>
              </dd></>)}
            {c.original_q4 && (<><dt className={capCls}>Original Q4</dt>
              <dd className="text-xs text-gray-700 dark:text-gray-300">
                Year as first printed <span className="font-mono tabular-nums">{fmtNum(c.original_q4.year.value, row.unit, units)}</span> − nine months as first printed{' '}
                <span className="font-mono tabular-nums">{fmtNum(c.original_q4.nine_months.value, row.unit, units)}</span>
                {c.original_q4.equations && c.original_q4.equations.tested > 0 ? ` · ${c.original_q4.equations.held}/${c.original_q4.equations.tested} of the statement’s equations hold with it` : ' · no complete equation to test'}
              </dd></>)}
            {c.sdb && (<><dt className={capCls}>Basis</dt>
              <dd className="text-amber-700 dark:text-amber-300">{c.sdb.label}
                <span className="block text-xs text-gray-600 dark:text-gray-400">{c.sdb.note}</span>
                {c.sdb.was_words && <span className="block text-xs text-gray-600 dark:text-gray-400">Why the presented basis has none: {c.sdb.was_words}.</span>}
                {c.sdb.also && <span className="block text-xs text-gray-600 dark:text-gray-400">{c.sdb.also.label}.</span>}
              </dd></>)}
            {row.note && c.kind !== 'printed' && (<><dt className={capCls}>Row</dt><dd className="text-gray-600 dark:text-gray-400">{row.note}</dd></>)}
          </dl>
        )}
        {p.basis_note && (
          <dl className="grid grid-cols-[auto,1fr] gap-x-5 gap-y-2.5">
            <dt className={capCls}>Column</dt>
            <dd className="text-gray-800 dark:text-gray-200">
              {p.basis_note.used === 'first_reported' ? 'As first reported' : p.basis_note.label}
              {p.basis_note.needs_review && <span className="text-amber-700 dark:text-amber-300"> — needs review</span>}
              {p.basis_note.blanks && (
                <span className="block text-xs text-gray-500">Blank cells: {p.basis_note.blanks.presented} as presented now ·{' '}
                  {p.basis_note.blanks.first_reported} as first reported — the column shows the one with fewer.</span>)}
              {p.qsum && (p.qsum.flagged > 0 || p.qsum.closed > 0) && (
                <span className="block text-xs text-gray-500">Quarters on mixed bases: {p.qsum.flagged} row{p.qsum.flagged === 1 ? '' : 's'} whose four quarters do not add to the year
                  {p.qsum.closed > 0 ? `; ${p.qsum.closed} closed by the reconciliation rows` : ''}.</span>)}
              {p.sdb && (
                <span className="block text-xs text-amber-700 dark:text-amber-300">{p.sdb.cells} figure{p.sdb.cells === 1 ? '' : 's'} in this column {p.sdb.kind === 'already_recast' ? 'are first prints the company made already on the recast basis (tag MIX)' : p.sdb.kind === 'pre_disposal' ? 'are shown before the disposal group, as first reported (tag PRE) — the totals are on the recast basis; the disposal-group row explains the difference' : 'are shown as first reported where the presented basis has none (tag 1ST)'}.
                  {p.sdb.closure && (p.sdb.closure.closes
                    ? <span className="block">The column closes: the parts add to {fmtNum(p.sdb.closure.first_reported_revenue, 'currency', units)} as first reported{p.sdb.closure.disposal != null ? `, less the disposal group ${fmtNum(p.sdb.closure.disposal, 'currency', units)}` : ''}{p.sdb.closure.presented_revenue != null ? ` = ${fmtNum(p.sdb.closure.presented_revenue, 'currency', units)} as presented now` : ''}.</span>
                    : <span className="block">The parts add to {fmtNum(p.sdb.closure.children_sum, 'currency', units)}; revenue as first reported is {fmtNum(p.sdb.closure.first_reported_revenue, 'currency', units)} — they differ.</span>)}
                </span>)}
              {p.basis_note.mixed && (
                <span className="block text-xs text-gray-500">{p.basis_note.mixed.recast_printed} rows are the company's printed recast quarter;{' '}
                  {p.basis_note.mixed.as_first_reported} rows stay as first reported (the company did not print them recast).</span>)}
            </dd>
            {p.basis_note.checks && (<>
              <dt className={capCls}>Checks</dt>
              <dd className="text-xs text-gray-700 dark:text-gray-300">
                <span className="block text-gray-500">{p.basis_note.checks.form} {p.basis_note.checks.date} — figures it prints for earlier periods, equal / tested:</span>
                {Object.entries(p.basis_note.checks.groups).map(([g, [t, m]]) => (
                  <span key={g} className="mr-3 inline-block whitespace-nowrap">{CHECK_NAME[g] || g} {m ? <span className="text-amber-700 dark:text-amber-300">{t - m}/{t} · {m} changed</span> : `${t}/${t}`}</span>))}
                {p.basis_note.checks.conflicts.length > 0 && <span className="mt-1 block text-amber-700 dark:text-amber-300">Does not fit the label: {p.basis_note.checks.conflicts.join(', ').replace(/_/g, ' ')}</span>}
              </dd></>)}
          </dl>
        )}
        {!p.basis_note && p.sdb && (
          <dl className="grid grid-cols-[auto,1fr] gap-x-5 gap-y-2.5">
            <dt className={capCls}>Basis</dt>
            <dd className="text-xs text-amber-700 dark:text-amber-300">{p.sdb.cells} figure{p.sdb.cells === 1 ? '' : 's'} in this column {p.sdb.kind === 'already_recast' ? 'are first prints the company made already on the recast basis (tag MIX)' : 'are shown as first reported where the presented basis has none (tag 1ST)'}.</dd>
          </dl>)}
        {!p.basis_note && p.qsum && (p.qsum.flagged > 0 || p.qsum.closed > 0) && (
          <dl className="grid grid-cols-[auto,1fr] gap-x-5 gap-y-2.5">
            <dt className={capCls}>Quarters</dt>
            <dd className="text-xs text-gray-700 dark:text-gray-300">Quarters on mixed bases: {p.qsum.flagged} row{p.qsum.flagged === 1 ? '' : 's'} whose four quarters do not add to the year
              {p.qsum.closed > 0 ? `; ${p.qsum.closed} closed by the reconciliation rows` : ''}.</dd>
          </dl>)}
        {data.bs_printed && data.bs_printed[p.key] && <PrintedBalance data={data} p={p} row={row} units={units} />}
        {c && (c.accessions || []).length > 0 && (
          <div>
            <div className={`${capCls} mb-2`}>Source filings</div>
            <ul className="space-y-2.5">
              {c.accessions.map((a) => (
                <li key={a.accession}>
                  {cik ? <a href={secUrl(cik, a.accession)} target="_blank" rel="noopener noreferrer"
                    className="text-brand-gold hover:underline">{a.form || 'Filing'}{a.date ? ` · ${a.date}` : ''}</a>
                    : <span>{a.form || 'Filing'}{a.date ? ` · ${a.date}` : ''}</span>}
                  <div className="font-mono text-[11px] text-gray-500">{a.accession}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </aside>
  );
}

function AddMenu({ menu, data, fam, gran, busy, onPick, onRemove, onClose }) {
  const ref = useRef(null);
  const [mode, setMode] = useState({ step: 'main' });
  const [pos, setPos] = useState({ left: menu.x, top: menu.y });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth, h = el.offsetHeight;
    setPos({ left: Math.max(8, Math.min(menu.x, window.innerWidth - w - 8)), top: Math.max(8, Math.min(menu.y, window.innerHeight - h - 8)) });
    const b = el.querySelector('button');
    if (b) b.focus();
  }, [menu, mode]);
  useEffect(() => {
    const out = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', out);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc); };
  }, [onClose]);

  const it = menu.it;
  const btn = 'block w-full rounded-md px-2.5 py-2 text-left text-[13px] text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:cursor-wait disabled:opacity-40 dark:text-gray-200 dark:hover:bg-gray-800 dark:focus:bg-gray-800';
  const head = (t) => <div className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">{t}</div>;
  let body;
  if (it.type === 'add') {
    body = (<>{head(it.a.label)}<button type="button" className={btn} disabled={busy} onClick={() => onRemove(it.a)}>Remove this row</button></>);
  } else if (mode.step === 'pick') {
    const rows = data.rows.filter((r) => r.id !== it.r.id && !r.bridge && !r.recon && (mode.kind === 'ratio' || r.unit === it.r.unit));
    body = (<>{head('Pick the other row')}{rows.map((r) => (
      <button key={r.id} type="button" className={btn} disabled={busy} onClick={() => onPick({ kind: mode.kind, src: it.r.id, ref: r.id })}>
        {r.label}{r.total && <small className="ml-1.5 text-gray-500">total</small>}</button>))}</>);
  } else if (mode.step === 'cagr') {
    body = (<>{head('CAGR over')}{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
      <button key={n} type="button" className={btn} disabled={busy} onClick={() => onPick({ kind: 'cagr', src: it.r.id, n })}>{n} year{n > 1 ? 's' : ''}</button>))}</>);
  } else {
    // the company's own revenue / total-assets row by its STANDARD-LINE mapping (the server's `default_ref`); none →
    // the user picks ("% of a row you pick…"), never a guess from a row's name
    const rev = data.default_ref ? data.rows.find((r) => r.id === data.default_ref) : null;
    const items = [['yoy', 'YoY %']];
    if (gran === 'interim') {
      items.push(['qoq', 'QoQ %']);
      if (fam !== 'BS') items.push(['ttm', 'TTM (last four quarters)']);
    } else items.push(['cagr', 'CAGR…']);
    if (rev && rev.id !== it.r.id && rev.unit === it.r.unit) items.push(['pct_default', `% of ${rev.label}`]);
    items.push(['pct_of', '% of a row you pick…'], ['diff', 'Difference vs a row…'], ['ratio', 'Ratio to a row…']);
    body = (<>{head(`Add under ${it.r.label}`)}{items.map(([k, t]) => (
      <button key={k} type="button" className={btn} disabled={busy} onClick={() => {
        if (k === 'pct_default') onPick({ kind: 'pct_of', src: it.r.id, ref: rev.id });
        else if (k === 'cagr') setMode({ step: 'cagr' });
        else if (k === 'pct_of' || k === 'diff' || k === 'ratio') setMode({ step: 'pick', kind: k });
        else onPick({ kind: k, src: it.r.id });
      }}>{t}</button>))}</>);
  }
  return (
    <div ref={ref} role="menu" style={pos}
      className="fixed z-50 max-h-[70vh] min-w-[230px] max-w-[calc(100vw-16px)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-xl dark:border-gray-700 dark:bg-[#161b22]">
      {body}
    </div>
  );
}

export default function FolioModel({ ticker, onUnits, onOpenQuestion }) {
  const { isDark } = useTheme();
  const pal = isDark ? PALETTE.dark : PALETTE.light;
  const phone = usePhone();
  const L = phone ? LAYOUT.phone : LAYOUT.desk;
  const [fam, setFam] = useState('IS_CI');
  const [gran, setGran] = useState('annual');
  const [basis, setBasis] = useState('auto');          // 'auto' | 'presented' | 'first_reported' (the server ignores it with the switch off)
  const [units, setUnitsState] = useState(() => readUnits() || (isPhoneNow() ? 'b' : 'm'));
  const setUnits = (u) => { setUnitsState(u); writeUnits(u); };
  useEffect(() => { if (onUnits) onUnits(units); }, [units, onUnits]);   // the page's subtitle names the unit too
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(false);  // the server answered 202: another request is building
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [panel, setPanel] = useState(null);
  const [menu, setMenu] = useState(null);
  const [toast, setToast] = useState('');
  const [busy, setBusy] = useState(false);          // an add / remove is being posted: the menu is disabled
  const [showDisposal, setShowDisposal] = useState(false);     // the disposal block under the income statement (#274): closed to start
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState('');  // a refused export, in plain words, until the next try / another view
  const [hoverRow, setHoverRow] = useState(-1);
  const [selRow, setSelRow] = useState(-1);
  const seq = useRef(0);                            // only the LATEST load's answer is applied
  const grid = useRef(null);
  const wrap = useRef(null);
  const wantRight = useRef(false);                  // a fresh statement: open on the newest period (see below)
  const gridW = useWidth(wrap);
  const below = useRef(null);                       // what sits under the grid (notes, legend)
  const gridH = useFitHeight(wrap, below);
  const scope = `${ticker}:${gran}:${fam}`;

  // a newer load (another company / statement / periods) or leaving the tab makes every older answer stale; a 202
  // "building" (another request holds the server's one build lock) is retried with backoff
  const load = useCallback((keep = false) => {
    if (!ticker) return Promise.resolve();
    const my = ++seq.current;
    const live = () => my === seq.current;
    if (!keep) { setData(null); setPanel(null); setSelRow(-1); }
    setErr(''); setLoading(true); setBuilding(false);
    let tries = 0;
    const attempt = () => folioService.getModel(ticker, gran, fam, basis).then((r) => {
      if (!live()) return undefined;
      if (r.status === 202 || r.data?.building) {
        tries += 1;
        setBuilding(true);
        if (tries > 7) { setErr('The statement is still being built. Try again in a minute.'); setLoading(false); return undefined; }
        return new Promise((res) => { setTimeout(res, Math.min(1000 * 2 ** (tries - 1), 16000)); }).then(() => (live() ? attempt() : undefined));
      }
      setData(r.data); setLoading(false); setBuilding(false);
      return undefined;
    }).catch((e) => {
      if (!live()) return;
      setErr(e?.response?.data?.detail || 'The statement could not be loaded.'); setLoading(false); setBuilding(false);
    });
    return attempt();
  }, [ticker, gran, fam, basis]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);
  useEffect(() => { setBasis('auto'); }, [ticker]);   // another company starts on the default again
  // the toggle exists only for a company with a recast / corrected event (the block says so; absent = switch off)
  const hasBasis = !!data?.two_basis?.has_events;
  const hasBadges = !!(hasBasis && data.periods.some((p) => p.basis_note));

  const say = (t) => { setToast(t); setTimeout(() => setToast(''), 2400); };
  const coll = useMemo(() => new Set(collapsed[scope] || []), [collapsed, scope]);
  const toggle = (id) => {
    const cur = new Set(collapsed[scope] || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    const next = { ...collapsed, [scope]: [...cur] };
    setCollapsed(next); writeCollapsed(next);
  };

  // the visible rows: a collapsed total hides the rows indented deeper under it (a running subtotal at its own level
  // stays, its deeper rows go); each added row sits under the row it was added after
  const items = useMemo(() => {
    if (!data) return [];
    const byId = Object.fromEntries(data.rows.map((r) => [r.id, r]));
    const kids = {};
    data.rows.forEach((r) => { if (r.parent) (kids[r.parent] = kids[r.parent] || []).push(r.id); });
    const hidden = new Set();
    const hideUnder = (id, depth, seen) => (kids[id] || []).forEach((k) => {
      const kr = byId[k];
      if (!kr || seen.has(k)) return;
      seen.add(k);
      if (kr.depth > depth) hidden.add(k);
      hideUnder(k, depth, seen);
    });
    data.rows.forEach((r) => { if (r.total && coll.has(r.id)) hideUnder(r.id, r.depth, new Set([r.id])); });
    // the reconciliation rows (first reported · less disposal group · presented now) follow the Basis control: Auto shows the
    // whole block (its columns mix bases, the block explains them); Now / First show that basis only, so the main rows
    // already are the figures of the chosen basis and only the disposal group stays, as one memo row (owner 2026-10-31)
    const pureBasis = data.basis === 'presented' || data.basis === 'first_reported';
    const memoLabel = data.basis === 'presented' ? 'Disposal group revenue, already taken out of the revenue above' : 'Disposal group revenue, taken out by the later recast';
    const out = [];
    data.rows.forEach((r) => {
      if (hidden.has(r.id)) return;
      if (pureBasis && r.recon) {
        if (r.recon === 'disposal') out.push({ type: 'row', r: { ...r, label: memoLabel } });
        return;
      }
      out.push({ type: 'row', r });
      data.added.filter((a) => a.after === r.id).forEach((a) => out.push({ type: 'add', a, r }));
    });
    data.added.filter((a) => !a.after || !byId[a.after]).forEach((a) => out.push({ type: 'add', a, r: byId[a.src] || null }));
    return out;
  }, [data, coll]);

  // FIGURE COLUMNS sized to their content in the active unit (the widest figure or header line of the statement, all
  // columns one width so the grid keeps one rhythm; never under today's 92 px on a desktop), then the LABEL column:
  // 300 px on a desktop, on a phone the rule below
  const figW = useMemo(() => {
    if (!data) return L.minFig;
    const font = `600 ${L.figFont}px ${MONO}`;                 // bold totals are the widest
    let w = 0;
    data.rows.forEach((r) => r.cells.forEach((c) => {
      if (c && c.value != null) w = Math.max(w, measure(font, aligned(fmtNum(c.value, r.unit, units))));
    }));
    data.added.forEach((a) => a.values.forEach((v) => { if (v != null) w = Math.max(w, measure(font, aligned(fmtAdded(v, a, units)))); }));
    data.periods.forEach((p) => {
      w = Math.max(w, measure(`600 ${L.headFont}px ${MONO}`, colHead(p, gran)), measure(`10px ${SANS}`, endLabel(p)));
    });
    return Math.max(L.minFig, Math.ceil(w) + PAD + 8);
  }, [data, units, gran, L]);
  // on a phone the label column takes EXACTLY what k whole figure columns leave — k = as many as fit beside a label
  // column of at least PHONE_LABEL_MIN (two readable lines) — so the newest columns end flush at the right edge with
  // no sliver of a hidden column beside the label column. 375 px in $bn: 3 columns, label ≈ 40–43 %; $m: 2 columns
  const phoneCols = gridW ? Math.max(1, Math.floor((gridW - PHONE_LABEL_MIN) / figW)) : 3;
  const labelW = phone && gridW ? gridW - phoneCols * figW : (phone ? 140 : 300);

  // the label layout of each visible row: where its text starts, whether it has a ▾ / ▸ and a ⋯, and its line(s)
  const labelFont = (it) => (it.type === 'add' ? `italic ${L.labelFont}px ${SANS}`
    : `${it.r.total || it.r.recon === 'revenue_f' || it.r.recon === 'revenue_p' ? 600 : 400} ${L.labelFont}px ${SANS}`);
  const layouts = useMemo(() => items.map((it) => {
    const depth = it.type === 'row' ? it.r.depth : (it.r ? it.r.depth + 1 : 1);
    const x0 = 8 + depth * L.indent;
    const hasToggle = it.type === 'row' && it.r.total;
    const dots = !(it.type === 'row' && (it.r.bridge || it.r.recon));
    // a COMPARABLE line (derived by code across restatements; its id carries the backend's `cmp~` key) shares its
    // as-filed twin's name, and the words that tell them apart sit at the END of its label — exactly what a narrow
    // column cuts. It gets a "CMP" tag in front instead; the full label stays in the tap panel and in a copy
    const cmp = it.type === 'row' && it.r.id.includes('|cmp~');
    const tagW = cmp ? Math.ceil(measure(`600 ${L.tagFont}px ${SANS}`, 'CMP')) + 6 : 0;
    const tagX = x0 + (hasToggle ? L.toggle : 0);
    const textX = tagX + tagW;
    const avail = labelW - textX - (dots ? L.dots : 8);
    const full = it.type === 'row' ? it.r.label : it.a.label;
    const label = cmp ? full.replace(/\s+—\s+comparable\b.*$/, '') : full;
    const lines = phone ? wrapTwo(labelFont(it), label, avail) : [fitText(labelFont(it), label, avail)];
    return { x0, hasToggle, dots, textX, lines, cmp, tagX };
  }), [items, labelW, phone, L]); // eslint-disable-line react-hooks/exhaustive-deps
  const rowHeight = useCallback((i) => (layouts[i] && layouts[i].lines.length > 1 ? L.rowH2 : L.rowH), [layouts, L]);

  const columns = useMemo(() => {
    if (!data) return [];
    return [{ title: TITLE[fam], id: 'label', width: labelW },
      ...data.periods.map((p) => ({ title: colHead(p, gran), id: p.key, width: figW }))];
  }, [data, fam, gran, labelW, figW]);

  // NEWEST PERIOD ON THE RIGHT, SHOWN FIRST — on every load (direct link, company / statement / periods switch, after a
  // 202 retry) and at every width. Not a timer: every new statement arms `wantRight`, and each time the grid reports its
  // real layout (onVisibleRegionChanged — it fires once the canvas has its size, and again on every resize or scroll)
  // the scroller is moved to its right end until it IS there; the user's first touch / wheel / key disarms it, so the
  // grid never fights a reader. (`.dvn-scroller` = Glide 6.0.3's scrolling element; the version is pinned.)
  // at the right end only once the scroller is laid out for THESE columns: a scroller not yet sized to its content
  // reads "at the end" at scrollLeft 0 (that disarmed the first version on a phone — FITB opened on FY17)
  const contentW = columns.reduce((t, c) => t + c.width, 0);
  const atRight = () => {
    const sc = wrap.current?.querySelector('.dvn-scroller');
    if (!sc || !contentW) return false;
    if (sc.clientWidth >= contentW - 8) return true;                 // everything fits: nothing to scroll
    return sc.scrollWidth >= contentW - 8 && sc.scrollLeft + sc.clientWidth >= sc.scrollWidth - 2;
  };
  const toRight = useCallback(() => {
    if (!wantRight.current) return;
    if (atRight()) { wantRight.current = false; return; }
    try { grid.current?.scrollTo(columns.length - 1, 0, 'horizontal', 0, 0, { hAlign: 'end' }); } catch { /* not mounted */ }
    const sc = wrap.current?.querySelector('.dvn-scroller');
    if (sc) sc.scrollLeft = sc.scrollWidth;
  }, [columns.length, contentW]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!data) return undefined;
    wantRight.current = true;
    // and every frame until it holds (≤ 4 s): the grid's layout events alone missed a scroller sized between them
    let frames = 0, id = 0;
    const tick = () => { toRight(); if (wantRight.current && frames++ < 240) id = requestAnimationFrame(tick); };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [data, units, toRight]);
  useEffect(() => { requestAnimationFrame(toRight); }, [gridW, labelW, figW, toRight]);
  const disarm = () => { wantRight.current = false; };

  const theme = useMemo(() => ({
    accentColor: pal.accent, accentFg: isDark ? '#000000' : '#ffffff', accentLight: pal.accentLight,
    textDark: pal.text, textMedium: pal.muted, textLight: pal.faint, textBubble: pal.text, bgIconHeader: pal.muted,
    fgIconHeader: pal.bg, textHeader: pal.muted, textGroupHeader: pal.text, textHeaderSelected: pal.text,
    bgCell: pal.bg, bgCellMedium: pal.surface2, bgHeader: pal.surface, bgHeaderHasFocus: pal.surface,
    bgHeaderHovered: pal.surface, bgBubble: pal.surface2, bgBubbleSelected: pal.surface, bgSearchResult: pal.accentLight,
    borderColor: 'rgba(0,0,0,0)', horizontalBorderColor: pal.line, headerBottomBorderColor: pal.rule,
    drilldownBorder: pal.line, linkColor: pal.accent, fontFamily: MONO, baseFontStyle: `${L.figFont}px`,
    headerFontStyle: `600 ${L.headFont}px`, editorFontSize: '13px', cellHorizontalPadding: PAD,
  }), [pal, isDark, L]);

  const getCellContent = useCallback(([col, row]) => {
    const it = items[row];
    const text = (s, extra = {}) => ({ kind: GridCellKind.Text, data: s, displayData: s, allowOverlay: false,
      readonly: true, ...extra });
    if (!it) return text('');
    if (col === 0) return text(it.type === 'row' ? it.r.label : it.a.label);   // drawn by drawLabel; copy reads this
    const ci = col - 1;
    const bold = it.type === 'row' && (it.r.total || it.r.recon === 'revenue_f' || it.r.recon === 'revenue_p') ? { baseFontStyle: `600 ${L.figFont}px`, textDark: pal.strong } : {};
    if (it.type === 'row') {
      const c = it.r.cells[ci];
      // a blank is drawn ONE way, "—" in the faint colour, whether the row has no cell in this period or a cell with
      // no figure; a tap tells them apart (the panel gives the reason)
      if (!c) return text(aligned('—'), { contentAlign: 'right', copyData: '', themeOverride: { ...bold, textDark: pal.faint } });
      const s = fmtNum(c.value, it.r.unit, units);
      const x = scaled(c.value, it.r.unit, units);
      // blank, or a figure that rounds to nothing in this unit (shown "0.0", never as no figure): faint
      const quiet = c.value == null || (x !== 0 && isZeroText(s));
      const other = c.sdb ? { baseFontStyle: `italic ${bold.baseFontStyle ? '600 ' : ''}${L.figFont}px` } : {};     // another basis: italic
      return text(aligned(s), { contentAlign: 'right', copyData: copyOf(c.value, it.r.unit, units),
        themeOverride: { ...bold, ...other, ...(quiet ? { textDark: pal.faint } : {}) } });
    }
    const raw = it.a.values[ci];
    const pct = it.a.unit === 'percent';
    const color = raw == null ? pal.faint : pct ? (Number(raw) >= 0 ? pal.pos : pal.neg) : pal.muted;
    return text(aligned(fmtAdded(raw, it.a, units)), { contentAlign: 'right', copyData: copyOf(raw, it.a.unit, units),
      themeOverride: { bgCell: pal.surface2, textDark: color } });
  }, [items, units, pal, L]);

  const rowTint = useCallback((row, label) => (row === selRow ? (label ? pal.selLabel : pal.sel)
    : row === hoverRow ? (label ? pal.hoverLabel : pal.hover) : null), [selRow, hoverRow, pal]);
  const getRowThemeOverride = useCallback((row) => {
    const t = rowTint(row, false);
    if (items[row]?.type === 'row' && items[row].r.recon) return { bgCell: t || pal.surface2 };   // the reconciliation rows sit on a faint band
    return t && items[row]?.type !== 'add' ? { bgCell: t } : undefined;
  }, [rowTint, items, pal]);

  // the accountant's rule: a hairline ABOVE a total, a double one above a grand total (a total no other total holds)
  const drawRule = (ctx, rect, it) => {
    if (it.type !== 'row' || !it.r.total) return;
    ctx.strokeStyle = pal.rule; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(rect.x, rect.y + 0.5); ctx.lineTo(rect.x + rect.width, rect.y + 0.5);
    if (!it.r.parent) { ctx.moveTo(rect.x, rect.y + 3.5); ctx.lineTo(rect.x + rect.width, rect.y + 3.5); }
    ctx.stroke();
  };

  // the LABEL cell is drawn here, whole (Glide's own text drawing is skipped for it): indent, ▾ / ▸, the label (two
  // lines on a phone) clipped with an ellipsis BEFORE the ⋯ area, then ⋯ (× on an added row)
  const drawLabel = (ctx, rect, it, lay, row, highlighted) => {
    ctx.save();
    ctx.beginPath(); ctx.rect(rect.x, rect.y, rect.width, rect.height); ctx.clip();
    ctx.fillStyle = rowTint(row, true) || (it.type === 'add' || (it.type === 'row' && it.r.recon) ? pal.surface2 : pal.surface);
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    if (highlighted) { ctx.fillStyle = pal.accentLight; ctx.fillRect(rect.x, rect.y, rect.width, rect.height); }
    drawRule(ctx, rect, it);
    ctx.textBaseline = 'middle';
    const mid = rect.y + rect.height / 2;
    if (lay.hasToggle) {
      ctx.fillStyle = pal.muted; ctx.font = `${phone ? 11 : 12}px ${SANS}`; ctx.textAlign = 'left';
      ctx.fillText(coll.has(it.r.id) ? '▸' : '▾', rect.x + lay.x0, mid);
    }
    ctx.textAlign = 'left';
    if (lay.cmp) {                   // the comparable line's tag, on the first text line
      ctx.fillStyle = pal.accent; ctx.font = `600 ${L.tagFont}px ${SANS}`;
      ctx.fillText('CMP', rect.x + lay.tagX, lay.lines.length > 1 ? mid - L.lineH / 2 : mid);
    }
    ctx.font = labelFont(it);
    ctx.fillStyle = it.type === 'add' ? pal.muted : it.r.total ? pal.strong : pal.text;
    const x = rect.x + lay.textX;
    if (lay.lines.length > 1) {
      ctx.fillText(lay.lines[0], x, mid - L.lineH / 2);
      ctx.fillText(lay.lines[1], x, mid + L.lineH / 2);
    } else ctx.fillText(lay.lines[0], x, mid);
    if (lay.dots) {                  // ⋯: tap it (or right-click the row) to add; never on a definition-change bridge
      ctx.fillStyle = pal.faint; ctx.font = `${phone ? 13 : 15}px ${SANS}`; ctx.textAlign = 'right';
      ctx.fillText(it.type === 'add' ? '×' : '⋯', rect.x + rect.width - (phone ? 6 : 10), mid);
    }
    ctx.restore();
  };
  const drawCell = useCallback((args, drawContent) => {
    const { ctx, rect, col, row, highlighted } = args;
    const it = items[row];
    if (col === 0 && it) { drawLabel(ctx, rect, it, layouts[row], row, highlighted); return; }
    drawContent();
    if (!it) return;
    ctx.save();
    drawRule(ctx, rect, it);
    if (it.type === 'row') {
      // THREE independent signals, so one never hides another (a restated figure that is also held for review used to
      // show only the restated dot): PENDING review = an amber bar on the cell's left edge; RESTATED = a gold corner
      // triangle top-right (a spreadsheet's note corner); DERIVED (not as filed) = a small dot just right of the figure
      const c = it.r.cells[col - 1];
      if (c && c.pending) {
        ctx.fillStyle = pal.warn;
        ctx.fillRect(rect.x + 1, rect.y + 3, 2.5, rect.height - 6);
      }
      if (c && c.first != null) {
        const r = rect.x + rect.width, t = rect.y;
        ctx.beginPath(); ctx.moveTo(r - 7, t); ctx.lineTo(r, t); ctx.lineTo(r, t + 7); ctx.closePath();
        ctx.fillStyle = pal.accent; ctx.fill();
      }
      if (c && c.recon && c.recon.tag) {          // a reconciliation figure's provenance: f filed · p printed · d derived (#274)
        ctx.font = `600 ${phone ? 8 : 9}px ${SANS}`; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillStyle = c.recon.status === 'no_tie' ? pal.warn : pal.faint;
        ctx.fillText(c.recon.status === 'no_tie' ? `≠${TAGLETTER[c.recon.tag]}` : TAGLETTER[c.recon.tag], rect.x + 5, rect.y + rect.height / 2 + 1);
      }
      if (c && c.sdb && SDB_TAG[c.sdb.kind]) {      // another basis than its neighbours: a small amber tag at the cell's left edge
        ctx.font = `700 ${phone ? 7 : 8}px ${SANS}`; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillStyle = pal.warn;
        ctx.fillText(SDB_TAG[c.sdb.kind], rect.x + 5, rect.y + rect.height / 2 + 1);
      }
      if (c && c.kind && c.kind !== 'direct' && c.kind !== 'printed' && !c.recon) {     // a printed column's figure is as filed
        ctx.beginPath();
        ctx.arc(rect.x + rect.width - 4, rect.y + rect.height / 2 + 1, 1.75, 0, 2 * Math.PI);
        ctx.fillStyle = pal.faint; ctx.fill();
      }
    }
    ctx.restore();
  }, [items, layouts, pal, coll, phone, L, rowTint]); // eslint-disable-line react-hooks/exhaustive-deps

  // the header: period ("FY25" / "Q1 FY26") over its end ("Aug 2025"), right-aligned like the figures; the label
  // column's header is the statement's name in the platform's uppercase label style
  const drawHeader = useCallback((args) => {
    const { ctx, rect, columnIndex } = args;
    ctx.save();
    ctx.fillStyle = pal.surface; ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    ctx.textBaseline = 'middle';
    if (columnIndex === 0) {
      const name = TITLE[fam].toUpperCase();
      const font = `600 10px ${SANS}`;
      // the UNIT, always on screen beside the figures (the frozen column never scrolls away): right-aligned in the
      // accent — the Units control can sit past the edge of a phone's pill row
      const unit = units === 'b' ? '$BN' : '$M';
      const ufont = `700 10px ${SANS}`;
      if ('letterSpacing' in ctx) ctx.letterSpacing = '0.6px';
      ctx.fillStyle = pal.accent; ctx.font = ufont; ctx.textAlign = 'right';
      ctx.fillText(unit, rect.x + rect.width - 8, rect.y + rect.height / 2);
      const room = rect.width - 16 - measure(ufont, unit) - 0.6 * unit.length - 8;
      ctx.fillStyle = pal.faint; ctx.font = font; ctx.textAlign = 'left';
      // letter-spaced like the platform's labels when it fits; a narrow phone column gets it plain, cut with an ellipsis
      const spaced = measure(font, name) + 1.4 * name.length <= room;
      if ('letterSpacing' in ctx) ctx.letterSpacing = spaced ? '1.4px' : '0px';
      ctx.fillText(spaced ? name : fitText(font, name, room), rect.x + 8, rect.y + rect.height / 2);
    } else if (data) {
      const p = data.periods[columnIndex - 1];
      const right = rect.x + rect.width - PAD - measure(`${L.figFont}px ${MONO}`, ' ');
      ctx.textAlign = 'right';
      // three lines when the statement has badges (period · end · basis), two otherwise — all columns share the layout
      const y0 = rect.y + rect.height / 2;
      const [y1, y2] = hasBadges ? [-14, 1] : [-7, 8];
      ctx.fillStyle = pal.text; ctx.font = `600 ${L.headFont}px ${MONO}`;
      ctx.fillText(colHead(p, gran), right, y0 + y1);
      ctx.fillStyle = pal.faint; ctx.font = `10px ${SANS}`;
      ctx.fillText(endLabel(p), right, y0 + y2);
      if (hasBadges && p.basis_note) {
        // the basis this column is on: 'As first reported' / 'Recast' / 'Changed since first reported' / 'Corrected'
        const font = `600 9px ${SANS}`;
        ctx.fillStyle = p.basis_note.used === 'first_reported' ? pal.warn : pal.accent; ctx.font = font;
        ctx.fillText(fitText(font, badgeText(p.basis_note, phone), rect.width - PAD - 4), right, y0 + 16);
      }
    }
    ctx.restore();
  }, [pal, fam, gran, data, L, units, hasBadges, phone]);

  const openMenu = (it, x, y) => {
    if (!it || busy || (it.type === 'row' && (it.r.bridge || it.r.recon))) return;
    setPanel(null); setMenu({ it, x, y });
  };
  const onCellClicked = ([col, row], ev) => {
    const it = items[row];
    if (!it) return;
    setSelRow(row);
    if (col === 0) {
      const lay = layouts[row];
      if (lay.dots && ev.localEventX > ev.bounds.width - Math.max(L.dots + 4, L.dotsHit)) {
        openMenu(it, ev.bounds.x + ev.bounds.width - 24, ev.bounds.y + ev.bounds.height); return;
      }
      if (lay.hasToggle && ev.localEventX < Math.max(lay.x0 + L.toggle + 2, L.toggleHit)) { toggle(it.r.id); return; }
      // the label itself: its full name + standard line (a phone may cut long names)
      if (it.type === 'row') { setMenu(null); setPanel({ row: it.r, label: true }); }
      return;
    }
    if (it.type === 'row') { setMenu(null); setPanel({ row: it.r, ci: col - 1 }); }
  };
  const onCellActivated = ([col, row]) => {
    const it = items[row];
    if (it && it.type === 'row' && col > 0) { setSelRow(row); setPanel({ row: it.r, ci: col - 1 }); }
  };
  const onCellContextMenu = ([, row], ev) => {
    ev.preventDefault();
    openMenu(items[row], ev.bounds.x + 20, ev.bounds.y + ev.bounds.height);
  };
  const onItemHovered = (args) => setHoverRow(args.kind === 'cell' ? args.location[1] : -1);

  const addRow = async ({ kind, src, ref, n }) => {
    if (busy) return;
    setMenu(null); setBusy(true);
    try {
      await folioService.addRow({ ticker, granularity: gran, family: fam, kind, src_row: src, ref_row: ref || '',
        n: n ?? null, after_row: src });
      await load(true);
      say('Row added');
    } catch (e) { say(e?.response?.data?.detail || 'Could not add the row.'); } finally { setBusy(false); }
  };
  const removeRow = async (a) => {
    if (busy) return;
    setMenu(null); setBusy(true);
    try { await folioService.removeRow(a.id); await load(true); say('Row removed'); } catch { say('Could not remove the row.'); }
    finally { setBusy(false); }
  };

  // Export: the three statements of the periods on screen, in the units on screen, with this user's added rows as live
  // Excel formulas (built on the server from the STORED statements — never a build)
  // layout 'model' = the flat data file for building a model: ONE basis for every column (the basis chosen above; Auto
  // switches basis column by column, so it reads as "as presented now" — the file name and its About sheet say which)
  const exportXlsx = async (layout) => {
    if (exporting || !ticker) return;
    setExporting(true); setExportErr('');
    try {
      const modelBasis = hasBasis ? (basis === 'first_reported' ? 'first_reported' : 'presented') : undefined;
      const r = layout === 'model'
        ? await folioService.exportXlsx(ticker, gran, units, modelBasis, 'model')
        : await folioService.exportXlsx(ticker, gran, units, hasBasis ? basis : undefined);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url; a.download = downloadName(r.headers, ticker, gran, units);
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      say('Exported');
    } catch (e) { setExportErr(await exportError(e)); } finally { setExporting(false); }
  };
  useEffect(() => { setExportErr(''); }, [ticker, gran]);

  const empty = data && !data.rows.length;
  return (
    <div className="space-y-3">
      {/* one row of controls: labelled segmented controls on a desktop; on a phone, compact pills that scroll sideways
          inside their own row (never wrapping, never widening the page) */}
      <div className="-mx-4 flex items-center gap-2 pr-4 md:mx-0 md:gap-5 md:pr-0">
        <div className="scrollbar-thin flex min-w-0 flex-1 flex-nowrap items-center gap-3 overflow-x-auto pb-1 pl-4 md:gap-5 md:pl-0">
          <Seg label="Statement" items={STATEMENTS} value={fam} onChange={setFam} phone={phone} />
          <Seg label="Periods" items={GRANS} value={gran} onChange={setGran} phone={phone} />
          <Seg label="Units" items={UNITS} value={units} onChange={setUnits} phone={phone} />
          {hasBasis && !phone && <Seg label="Basis" items={BASES} value={basis} onChange={setBasis} phone={phone} desk />}
        </div>
        {/* always in view: beside the pills (which scroll sideways on a phone), an icon on a phone, labelled on a desktop */}
        <button type="button" onClick={() => exportXlsx()} disabled={exporting || !data}
          aria-label={`Export ${data?.company?.ticker || ticker} to Excel — ${gran === 'annual' ? 'annual' : 'quarterly'}, all three statements`}
          title="Download the three statements as .xlsx (added rows as live formulas)"
          className="mb-1 inline-flex h-11 min-w-[44px] flex-none items-center justify-center gap-1.5 rounded-md border border-gray-200 px-2.5 text-[11px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 md:h-auto md:py-1 tracking-wide text-gray-600 transition-colors hover:border-brand-gold/60 hover:text-brand-gold disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700/80 dark:text-gray-400">
          <DownloadIcon />
          <span className={phone ? 'sr-only' : ''}>{exporting ? 'Exporting…' : 'Export .xlsx'}</span>
        </button>
        <button type="button" onClick={() => exportXlsx('model')} disabled={exporting || !data} data-testid="export-model"
          aria-label={`Export ${data?.company?.ticker || ticker} model data to Excel — ${gran === 'annual' ? 'annual' : 'quarterly'}, one basis`}
          title="Model data: flat tables, one basis for every column, no reconciliation rows, with live footing checks"
          className="mb-1 inline-flex h-11 min-w-[44px] flex-none items-center justify-center gap-1.5 rounded-md border border-gray-200 px-2.5 text-[11px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 md:h-auto md:py-1 tracking-wide text-gray-600 transition-colors hover:border-brand-gold/60 hover:text-brand-gold disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700/80 dark:text-gray-400">
          <DownloadIcon />
          <span className={phone ? 'sr-only' : ''}>Model data</span>
        </button>
      </div>
      {/* a phone: the basis toggle on its own row — in the sideways-scrolling pill row it sat past the right edge, out of sight */}
      {hasBasis && phone && (
        <div className="flex items-center gap-2" data-testid="basis-row">
          <span className="text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Basis</span>
          <Seg label="Basis" items={BASES} value={basis} onChange={setBasis} phone={phone} />
        </div>
      )}
      {exportErr && <div role="alert" className="text-xs text-red-600 dark:text-red-400">{exportErr}</div>}
      <div ref={wrap} onPointerDown={disarm} onWheel={disarm} onKeyDown={disarm} data-label-w={labelW} data-fig-w={figW}
        style={{ height: gridH }}
        className="relative w-full overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0d1117]">
        {!data && !err && <GridSkeleton phone={phone} building={building} />}
        {err && !data && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="text-sm text-gray-700 dark:text-gray-300">{err}</div>
            <button type="button" onClick={() => load()}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:border-brand-gold/60 dark:border-gray-700 dark:text-gray-300">Try again</button>
          </div>
        )}
        {empty && (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500">
            This statement has no rows for {data.company?.name || ticker} yet.</div>
        )}
        {data && !empty && (
          <DataEditor ref={grid} columns={columns} rows={items.length} getCellContent={getCellContent} drawCell={drawCell}
            drawHeader={drawHeader} freezeColumns={1} rowHeight={rowHeight} headerHeight={hasBadges ? 58 : 44} width="100%" height="100%"
            theme={theme} verticalBorder={false} getRowThemeOverride={getRowThemeOverride} onItemHovered={onItemHovered}
            smoothScrollX smoothScrollY getCellsForSelection rangeSelect="rect" columnSelect="none"
            rowSelect="none" onCellClicked={onCellClicked} onCellActivated={onCellActivated}
            onCellContextMenu={onCellContextMenu} onVisibleRegionChanged={toRight} />
        )}
        {loading && data && <div className="absolute right-3 top-1.5 text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">updating</div>}
      </div>
      <div ref={below} className="space-y-3">
      {err && data && <div className="text-sm text-red-600 dark:text-red-400">{err}</div>}
      {/* a series that spans a change of registrant (register #198): which earlier registrant filed the older figures */}
      {(data?.company?.predecessors || []).map((p) => (
        <div key={p.cik} className="text-[11px] text-gray-500 dark:text-gray-400">
          Until {p.until}: filed as <span className="text-gray-700 dark:text-gray-300">{p.name}</span> (CIK {p.cik}) — predecessor
          registrant, succeeded by {p.succession_form}{' '}
          <a href={secUrl(p.cik, p.succession_accession)} target="_blank" rel="noopener noreferrer"
            className="text-brand-gold hover:underline">{p.succession_accession}</a>
        </div>
      ))}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-1.5"><i className="inline-block h-0 w-0" style={{ borderTop: `7px solid ${pal.accent}`, borderLeft: '7px solid transparent' }} />{data?.cause_labels ? [...new Set(data.rows.flatMap((r) => r.cells.map((c) => (c && c.first != null ? causeLabel(c.cause) : null))).filter(Boolean))].join(' / ') || CAUSE[''] : 'Restated'}</span>
        <span className="inline-flex items-center gap-1.5"><i className="inline-block h-3 w-[3px]" style={{ background: pal.warn }} />Pending review</span>
        <span className="inline-flex items-center gap-1.5"><i className="inline-block h-[5px] w-[5px] rounded-full" style={{ background: pal.faint }} />Derived</span>
        {data?.rows?.some((r) => r.sdb) && <span><span className="font-mono text-amber-700 dark:text-amber-300">PRE</span> before the disposal group, as first reported · <span className="font-mono text-amber-700 dark:text-amber-300">1ST</span> as first reported · <span className="font-mono text-amber-700 dark:text-amber-300">MIX</span> first reported already recast — italic figures are on another basis than the totals</span>}
        {data?.bs_printed && <span>⚠ in a badge: the printed balance-sheet column does not foot</span>}
        {hasBadges && <span>Column badge: the basis it is on — {data.basis === 'auto' ? 'auto picks the one with fewer blanks' : data.basis === 'first_reported' ? 'the original figures' : 'the latest presentation'}</span>}
        {data?.disposal && data.disposal.years?.length > 0 && <span><span className="font-mono">f</span> filed · <span className="font-mono">p</span> printed · <span className="font-mono">d</span> derived · ≠ does not tie</span>}
        {data?.correction && <span>Correction of prior-period error: the later presentation − the first, exactly — always needs review</span>}
        {data?.polish?.cash_bridge && Object.keys(data.polish.cash_bridge).length > 0 && <span>Cash bridge: continuing + disposal group = including, as tagged</span>}
        <span>— blank: tap it for the reason</span>
        <span className="hidden md:inline">{units === 'b' ? 'USD billions' : 'USD millions'} · shares in millions · per share in USD</span>
        <span className="hidden md:inline">Tap a figure for its source, a name for the full name · ⋯ or right-click to add a row · drag + {navigator.platform?.startsWith('Mac') ? '⌘' : 'Ctrl'}+C copies to Excel</span>
        <span className="md:hidden">Tap a figure for its source, a name for its full name · ⋯ adds a row</span>
      </div>
      {data?.disposal && data.disposal.years?.length > 0 && (
        <button type="button" onClick={() => setShowDisposal((v) => !v)} aria-expanded={showDisposal}
          className="inline-flex min-h-[44px] items-center gap-1.5 text-left text-[12px] text-brand-gold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 md:min-h-0">
          <span aria-hidden="true">{showDisposal ? '▾' : '▸'}</span> Disposal group — as the filings tag it ({data.disposal.years.length} fiscal year{data.disposal.years.length > 1 ? 's' : ''})
        </button>)}
      </div>
      {data?.disposal && showDisposal && <DisposalBlock block={data.disposal} cik={data.company?.cik} units={units} />}
      {data && <SidePanel panel={panel} data={data} gran={gran} units={units} onClose={() => { setPanel(null); setSelRow(-1); }}
        onOpenQuestion={(q) => { if (onOpenQuestion) onOpenQuestion(q); }} />}
      {data && menu && <AddMenu key={`${menu.x}:${menu.y}`} menu={menu} data={data} fam={fam} gran={gran} busy={busy} onPick={addRow}
        onRemove={removeRow} onClose={() => setMenu(null)} />}
      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-gray-900 px-3.5 py-2 text-sm text-white dark:bg-gray-100 dark:text-gray-900">{toast}</div>}
    </div>
  );
}
